import {
  Flex,
  Accordion,
  FileUploadButton,
  MultipleFileUpload,
  Text,
  Input,
  InputDescriptionWrap,
} from 'components'
import { Button } from '@nextretreat/ui-components/dist/Button'
import styled from '@emotion/styled'
import { rem } from 'polished'
import { FieldArray, useFormikContext } from 'formik'
import { IVenueFormValues } from './FormPage'
import { EmptyDiv } from './GeneralInfoStep'
import get from 'lodash.get'
import { IRoom, tableOrganisation } from 'utils'
import { inputDescriptions } from './inputDescriptions'

const StyledFlex = styled(Flex)`
  flex-direction: column;
  gap: ${rem(20)};
`

const StyledFlexWithMargin = styled(StyledFlex)`
  margin-top: ${rem(20)};
  padding: 0 5px;
`

const Row = styled(Flex)`
  gap: ${rem(10)};

  & > * {
    flex-grow: 1;
    flex-basis: 100%;
  }
`

export const MeetingRoomsStep = () => {
  const { values, setFieldValue } = useFormikContext<IVenueFormValues>()

  const meetingRooms = values.meetingRooms as IRoom[]

  return (
    <>
      <Text fontSize={18} fontWeight="500" textAlign="left">
        Tell us about your meeting room / communal space.
      </Text>
      <Text fontSize={14} textAlign="left">
        Specify the details of the meeting room inside your property. Also,
        please make sure to include some photos.
      </Text>

      <FieldArray name="meetingRooms">
        {({ remove, push }) => (
          <StyledFlex>
            {meetingRooms?.length > 0 &&
              meetingRooms.map(
                ({ title }: { title: string }, index: number) => {
                  const roomName = `meetingRooms.${index}`

                  const roomCapacity = get(values, `${roomName}.capacity`)

                  return (
                    <Accordion
                      key={index}
                      isInitialOpen={index === 0}
                      title={`Meeting Room ${index + 1}${
                        title ? ' - ' + title : ''
                      }`}
                    >
                      <StyledFlexWithMargin>
                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.name}
                        >
                          <Row>
                            <Input
                              label="Title"
                              placeholder="Enter title"
                              name={`${roomName}.title`}
                            />

                            <EmptyDiv />
                          </Row>
                        </InputDescriptionWrap>

                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.description}
                        >
                          <Input.Textarea
                            isBlock
                            label="Description"
                            name={`${roomName}.description`}
                            placeholder="Enter description"
                          />
                        </InputDescriptionWrap>

                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.size}
                        >
                          <Input
                            label="Meeting room size"
                            isBlock
                            type="number"
                            min={0}
                            name={`${roomName}.squareMeters`}
                            suffix="m²"
                          />
                        </InputDescriptionWrap>

                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.quantity}
                        >
                          <Input.Number
                            min={0}
                            label="Quantity"
                            name={`${roomName}.quantity`}
                            value={get(values, `${roomName}.quantity`)}
                            onChange={(val) =>
                              setFieldValue(`${roomName}.quantity`, val)
                            }
                          />
                        </InputDescriptionWrap>

                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.capacity}
                        >
                          <Input.Number
                            min={0}
                            label="Maximum capacity"
                            name={`${roomName}.capacity`}
                            value={get(values, `${roomName}.capacity`)}
                            onChange={(val) =>
                              setFieldValue(`${roomName}.capacity`, val)
                            }
                          />
                        </InputDescriptionWrap>

                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.thumbnail}
                        >
                          <FileUploadButton
                            name={`${roomName}.thumbnail`}
                            fileObj={get(values, `${roomName}.thumbnail`)}
                            onSubmit={(file) =>
                              setFieldValue(`${roomName}.thumbnail`, file)
                            }
                          />
                        </InputDescriptionWrap>

                        <InputDescriptionWrap
                          text={inputDescriptions.meetingRooms.photos}
                        >
                          <FieldArray name={`${roomName}.photos`}>
                            {({ remove: removePhoto, push: pushPhoto }) => (
                              <MultipleFileUpload
                                name={`${roomName}.photos`}
                                onRemoveFile={removePhoto}
                                onDrop={(acceptedFiles) =>
                                  acceptedFiles.forEach((file) =>
                                    pushPhoto({ file })
                                  )
                                }
                              />
                            )}
                          </FieldArray>
                        </InputDescriptionWrap>

                        <Flex flexDirection="row-reverse">
                          <Button.Secondary onClick={() => remove(index)}>
                            Remove meeting room {index + 1}
                          </Button.Secondary>
                        </Flex>

                        {!values.generalInfo.isEntireHouse && (
                          <FieldArray name={`${roomName}.variations`}>
                            {({
                              remove: removeVariation,
                              push: pushVariation,
                            }) => (
                              <StyledFlex>
                                {meetingRooms[index].variations?.length > 0 &&
                                  meetingRooms[index].variations.map(
                                    (_: unknown, variationIndex: number) => {
                                      const variationName = `${roomName}.variations.${variationIndex}`

                                      return (
                                        <Accordion
                                          key={variationIndex}
                                          isInitialOpen={
                                            index === 0 && variationIndex === 0
                                          }
                                          title={`Variation ${
                                            variationIndex + 1
                                          }`}
                                        >
                                          <StyledFlexWithMargin>
                                            <InputDescriptionWrap
                                              text={
                                                inputDescriptions.meetingRooms
                                                  .variations.tableOrganisation
                                              }
                                            >
                                              <Input.Select
                                                isBlock
                                                label="Seating Organization"
                                                placeholder="Select seating organization"
                                                name={`${variationName}.meal`}
                                                onChange={(val) =>
                                                  setFieldValue(
                                                    `${variationName}.meal`,
                                                    val
                                                  )
                                                }
                                                value={get(
                                                  values,
                                                  `${variationName}.meal`
                                                )}
                                                options={Object.keys(
                                                  tableOrganisation
                                                ).map((type) => ({
                                                  label:
                                                    // @ts-ignore
                                                    tableOrganisation[type],
                                                  value: type,
                                                }))}
                                              />
                                            </InputDescriptionWrap>

                                            <InputDescriptionWrap
                                              text={
                                                inputDescriptions.meetingRooms
                                                  .variations.capacity
                                              }
                                            >
                                              <Input.Number
                                                label="Capacity"
                                                max={roomCapacity}
                                                min={0}
                                                name={`${variationName}.capacity`}
                                                value={get(
                                                  values,
                                                  `${variationName}.capacity`
                                                )}
                                                onChange={(val) =>
                                                  setFieldValue(
                                                    `${variationName}.capacity`,
                                                    val
                                                  )
                                                }
                                              />
                                            </InputDescriptionWrap>

                                            <InputDescriptionWrap
                                              text={
                                                inputDescriptions.meetingRooms
                                                  .variations.description
                                              }
                                            >
                                              <Input
                                                isBlock
                                                label="Description"
                                                name={`${variationName}.description`}
                                                placeholder="Enter description"
                                              />
                                            </InputDescriptionWrap>

                                            <InputDescriptionWrap
                                              text={
                                                inputDescriptions.meetingRooms
                                                  .variations.lowRate
                                              }
                                            >
                                              <Input
                                                isBlock
                                                type="number"
                                                label="Lowest Price"
                                                min={0}
                                                suffix="€"
                                                name={`${variationName}.lowRate`}
                                                placeholder="Enter lowest price"
                                              />
                                            </InputDescriptionWrap>

                                            <Flex flexDirection="row-reverse">
                                              <Button.Secondary
                                                onClick={() =>
                                                  removeVariation(
                                                    variationIndex
                                                  )
                                                }
                                              >
                                                Remove Variation{' '}
                                                {variationIndex + 1}
                                              </Button.Secondary>
                                            </Flex>
                                          </StyledFlexWithMargin>
                                        </Accordion>
                                      )
                                    }
                                  )}
                                <InputDescriptionWrap
                                  text={
                                    inputDescriptions.meetingRooms.variations
                                      .add
                                  }
                                >
                                  <Button.Primary
                                    type="button"
                                    onClick={() =>
                                      pushVariation({
                                        description: '',
                                      })
                                    }
                                  >
                                    Add variation
                                    {meetingRooms[index].variations?.length ===
                                    0
                                      ? ' (required)'
                                      : ''}
                                  </Button.Primary>
                                </InputDescriptionWrap>
                              </StyledFlex>
                            )}
                          </FieldArray>
                        )}
                      </StyledFlexWithMargin>
                    </Accordion>
                  )
                }
              )}

            <div>
              <Button.Primary
                type="button"
                onClick={() => push({ title: '', description: '' })}
              >
                Add room
              </Button.Primary>
            </div>
          </StyledFlex>
        )}
      </FieldArray>
    </>
  )
}
